import { useEffect, useRef } from 'react'
import Styles from './ChatMessages.module.scss'
import { collectResults } from 'utils/query'
import { useInfiniteMessages } from 'hooks/queries/chat/useInfiniteMessages'
import { sortBy } from 'lodash'
import { Message } from './Message/Message'
import { LoadingState } from './LoadingState'
import { ChatMessagesProps } from './Types'
import { Suggestions } from './Suggestions'
import { AIJobStatus } from 'types'
import { useSyncChatComparison } from 'hooks/queries/chat/useSyncChatComparison'

const ChatMessages = ({ threadId, setValue, isNextGen, context, showCaptionSuggestion }: ChatMessagesProps) => {
  const messagesQuery = useInfiniteMessages({ threadId, items: 30 })
  const messages = collectResults(messagesQuery, (data) => data.messages)
  const sortedMessages = sortBy(messages, (message) => message.created_at)
  const { hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } = messagesQuery
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const lastMessageContent = sortedMessages.length > 0 ? sortedMessages[sortedMessages.length - 1].content : null
  const lastMessageContentRef = useRef(lastMessageContent)
  const bottomRef = useRef<HTMLDivElement>(null)
  const initialLoad = useRef(true)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isInProgress = sortedMessages.some(
    (message) => message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status),
  )

  useSyncChatComparison({ messages: sortedMessages, threadId })

  useEffect(() => {
    if (lastMessageContent !== lastMessageContentRef.current) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
    lastMessageContentRef.current = lastMessageContent
  }, [lastMessageContent])

  useEffect(() => {
    if (initialLoad.current && sortedMessages.length > 0) {
      initialLoad.current = false
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [sortedMessages])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={Styles.messagesContainer}>
      <div className={Styles.loadingContainer}>
        {hasNextPage && <div ref={sentinelRef} data-testid="scroll-sentinel" style={{ height: 1 }} />}
        {(isLoading || isFetchingNextPage) && <LoadingState count={isLoading ? 2 : 1} />}
      </div>
      <div className={Styles.messages} ref={messagesContainerRef}>
        {sortedMessages.map((message, index) => (
          <Message
            key={message.id}
            message={message}
            isLastMessage={index === sortedMessages.length - 1}
            isNextGen={isNextGen}
            context={context}
            isDisabled={isInProgress}
          />
        ))}
        {!isInProgress && sortedMessages.length === 0 && (
          <Suggestions setValue={setValue} isNextGen={isNextGen} showCaptionSuggestion={showCaptionSuggestion} />
        )}
      </div>
      <div ref={bottomRef} />
    </div>
  )
}

export { ChatMessages }
